import { Controller, FieldErrors } from 'react-hook-form';
import {
  Autocomplete,
  DateInput,
  DateInputSize,
  InputSize,
  InputText,
  Select,
  SelectSize,
  Textarea,
} from '@ui/src';
import { NewApplicationStatus } from '../../../../hoc/new-application/types';
import InfoRow from '../../info-row';
import StepPartBlock from './step-part-block';
import { PepQuestionnarieTypes } from '../../types';
import { CorporateQuestionnarieFormValues } from '../../../../hoc/customers/types';

import cn from '../../new-application.module.css';

const PepQuestionnarie = ({
  questionnarieControl,
  questionnarieClearErrors,
  status,
  isEditable,
  questionnarieErrors,
  questionnarieFields,
  countries,
  relationshipToTheCustomerDictionary,
  questionnarieSetValue,
  isIndividual,
}: PepQuestionnarieTypes) => (
  <StepPartBlock title="PEP’s Questionnaire" zIndex={isIndividual ? 12 : 16}>
    <>
      <InfoRow
        title={
          isIndividual
            ? 'Were/are you or relative/associate a PEP?'
            : 'Were or are you or an immediate close relative or a close associate, a PEP?'
        }
        id="isPepOrAssociate"
        lessPadding
      >
        <Controller
          control={questionnarieControl}
          name="isPepOrAssociate"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={[
                {
                  name: 'Yes',
                  value: 'yes',
                },
                {
                  name: 'No',
                  value: 'no',
                },
              ]}
              value={value as string}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                questionnarieClearErrors(['isPepOrAssociate']);

                if (e.target.value === 'no') {
                  questionnarieClearErrors([
                    'nameOfPep',
                    'relToPep',
                    'orgEmployerAndPosition',
                    'positionHeldCountryId',
                    'politicalFunctionPeriodFrom',
                    'politicalFunctionPeriodTo',
                    'abilityMoveGovernmentFunds',
                    'abilityMoveGovernmentFundsDetails',
                  ]);

                  if (!isIndividual) {
                    questionnarieClearErrors(['relToCustomerId']);
                  }

                  if (!isIndividual) {
                    questionnarieSetValue('relToCustomerId', '');
                  }

                  questionnarieSetValue('nameOfPep', '');
                  questionnarieSetValue('relToPep', '');
                  questionnarieSetValue('orgEmployerAndPosition', '');
                  questionnarieSetValue('positionHeldCountryId', '');
                  questionnarieSetValue('politicalFunctionPeriodFrom', '');
                  questionnarieSetValue('politicalFunctionPeriodTo', '');
                  questionnarieSetValue('abilityMoveGovernmentFunds', '');
                  questionnarieSetValue(
                    'abilityMoveGovernmentFundsDetails',
                    '',
                  );
                }
              }}
              selectWrapperClassName={
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
                  ? cn.selectDisabled
                  : ''
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              error={questionnarieErrors?.isPepOrAssociate?.message}
              fullWidth
              borderless
            />
          )}
        />
      </InfoRow>
      {questionnarieFields?.isPepOrAssociate === 'yes' && (
        <>
          <InfoRow title="Name of the PEP" id="nameOfPep" lessPadding>
            <Controller
              control={questionnarieControl}
              name="nameOfPep"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter name..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['nameOfPep']);
                  }}
                  className="borderless"
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.nameOfPep?.message}
                />
              )}
            />
          </InfoRow>
          {!isIndividual && (
            <InfoRow
              title="Relationship to the Customer"
              id="relToCustomerId"
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="relToCustomerId"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Select
                    name={name}
                    placeholder="Select an option..."
                    list={
                      relationshipToTheCustomerDictionary?.map((item) => ({
                        name: item.value,
                        value: item.id,
                      })) || []
                    }
                    value={value}
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['relToCustomerId']);
                    }}
                    selectWrapperClassName={
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED
                        ? cn.selectDisabled
                        : ''
                    }
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    tabIndex={
                      !isEditable &&
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    error={
                      (
                        questionnarieErrors as FieldErrors<CorporateQuestionnarieFormValues>
                      )?.relToCustomerId?.message
                    }
                    fullWidth
                    borderless
                  />
                )}
              />
            </InfoRow>
          )}

          <InfoRow
            title={
              isIndividual
                ? 'Relationship (if relative/associate of a PEP)'
                : 'Relationship to PEP (optional)'
            }
            id="relToPep"
            className={cn.noFlex}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="relToPep"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Textarea
                  name={name}
                  title=""
                  placeholder="Enter description..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['relToPep']);
                  }}
                  className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                  error={questionnarieErrors?.relToPep?.message}
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title="Organization/employer and position"
            id="orgEmployerAndPosition"
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="orgEmployerAndPosition"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter name..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['orgEmployerAndPosition']);
                  }}
                  className="borderless"
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.orgEmployerAndPosition?.message}
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title="Country of the position held"
            id="positionHeldCountryId"
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="positionHeldCountryId"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Autocomplete
                  name={name}
                  placeholder="Select country..."
                  list={countries}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['positionHeldCountryId']);
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.positionHeldCountryId?.message}
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title={
              isIndividual
                ? 'Period held'
                : 'Period in which this individual has held the political or public function'
            }
            id="politicalFunctionPeriodFrom"
            lessPadding
          >
            <div className={cn.pepPeriodRow}>
              <div className={cn.pepPeriodItem}>
                <Controller
                  control={questionnarieControl}
                  name="politicalFunctionPeriodFrom"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <DateInput
                      placeholder="From"
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'politicalFunctionPeriodFrom',
                        ]);
                      }}
                      size={DateInputSize.SMALL}
                      error={
                        questionnarieErrors?.politicalFunctionPeriodFrom
                          ?.message
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      withoutCustomSelector
                      borderless
                    />
                  )}
                />
              </div>
              <div className={cn.pepPeriodItem}>
                <Controller
                  control={questionnarieControl}
                  name="politicalFunctionPeriodTo"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <DateInput
                      placeholder="To"
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['politicalFunctionPeriodTo']);
                      }}
                      size={DateInputSize.SMALL}
                      error={
                        questionnarieErrors?.politicalFunctionPeriodTo?.message
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      withoutCustomSelector
                      borderless
                    />
                  )}
                />
              </div>
            </div>
          </InfoRow>
          <InfoRow
            title={
              isIndividual
                ? 'Ability to move government funds'
                : 'Ability to move government funds during the period'
            }
            id="abilityMoveGovernmentFunds"
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="abilityMoveGovernmentFunds"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['abilityMoveGovernmentFunds']);
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? cn.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={
                    questionnarieErrors?.abilityMoveGovernmentFunds?.message
                  }
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          {questionnarieFields?.abilityMoveGovernmentFunds === 'yes' && (
            <InfoRow
              title="Provide details"
              id="abilityMoveGovernmentFundsDetails"
              className={cn.noFlex}
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="abilityMoveGovernmentFundsDetails"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Textarea
                    name={name}
                    title=""
                    placeholder="Enter details..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors([
                        'abilityMoveGovernmentFundsDetails',
                      ]);
                    }}
                    className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                    error={
                      questionnarieErrors?.abilityMoveGovernmentFundsDetails
                        ?.message
                    }
                    tabIndex={
                      !isEditable &&
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
          )}
        </>
      )}
    </>
  </StepPartBlock>
);

export default PepQuestionnarie;
